import { useEffect, useRef, useState } from "react";

interface UseInViewOptions {
  /** Root margin — how far before the element enters the viewport to trigger (default: "200px") */
  rootMargin?: string;
  /** Once triggered, stays true forever (default: true) */
  once?: boolean;
  /** Fallback timeout in ms — forces inView=true if observer doesn't fire (default: 1500) */
  fallbackTimeout?: number;
}

/**
 * Returns a ref to attach to a DOM element and a boolean `inView`
 * that becomes true when the element enters the viewport.
 * Used for lazy-loading sections below the fold.
 *
 * Mobile-safe: includes a fallback timeout for iOS Safari where
 * IntersectionObserver may not fire reliably on initial load.
 */
export function useInView<T extends HTMLElement = HTMLDivElement>(
  options: UseInViewOptions = {}
): [React.RefObject<T>, boolean] {
  const { rootMargin = "400px", once = true, fallbackTimeout = 1500 } = options;
  const ref = useRef<T>(null);
  const [inView, setInView] = useState(false);
  // إضافة mounted ref لمنع تكرار setInView بعد unmount
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    const el = ref.current;
    if (!el) {
      // No element yet — force true after timeout
      const t = setTimeout(() => {
        if (mountedRef.current) setInView(true);
      }, fallbackTimeout);
      return () => {
        clearTimeout(t);
      };
    }

    // If IntersectionObserver is not supported, just show everything
    if (typeof IntersectionObserver === "undefined") {
      setInView(true);
      return;
    }

    let triggered = false;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          triggered = true;
          if (mountedRef.current) setInView(true);
          if (once) observer.disconnect();
        } else if (!once) {
          if (mountedRef.current) setInView(false);
        }
      },
      { rootMargin }
    );

    observer.observe(el);

    // Fallback: if observer never fires within timeout, force inView=true
    // This handles iOS Safari quirks and hidden/offscreen elements
    const fallback = setTimeout(() => {
      if (!triggered && mountedRef.current) {
        setInView(true);
      }
    }, fallbackTimeout);

    return () => {
      observer.disconnect();
      clearTimeout(fallback);
      mountedRef.current = false;
    };
  }, [rootMargin, once, fallbackTimeout]);

  return [ref, inView];
}
